import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Organization } from '@Types/api/common/dictionary';
import { Currency } from '@Types/api/common/dictionary/Currency';
import {
  TypeDict,
  CompanyDict,
  CountryDict,
  ProjectNDepartments,
  DeptProjList,
} from 'types';

interface DictionaryState {
  requisitionRequestType: TypeDict[];
  requestType: TypeDict[];
  leaveType: TypeDict[];
  leaveStatuses: TypeDict[];
  itReqItems: TypeDict[];
  simCapValue: TypeDict[];
  itAccessories: TypeDict[];
  passportType: TypeDict[];
  passportStatuses: TypeDict[];
  requisitionStatuses: TypeDict[];
  salaryType: TypeDict[];
  salaryStatuses: TypeDict[];
  itReqStatuses: TypeDict[];
  overseasStatuses: TypeDict[];
  overseasSponsors: TypeDict[];
  countries: CountryDict[];
  companies: CompanyDict[];
  resumptionStatuses: TypeDict[];
  resumptionWorkShift: TypeDict[];
  attendanceReason: TypeDict[];
  attendanceStatuses: TypeDict[];
  attendanceType: TypeDict[];
  workLocationType: TypeDict[];
  projectNDepartments: ProjectNDepartments[];
  departmentsProjects: ProjectNDepartments[];
  deptProjList: DeptProjList[];
  resignationReasons: TypeDict[];
  resignationStatuses: TypeDict[];
  organizations: Organization[];
  costCenterType: TypeDict[];
  warrantyType: TypeDict[];
  mrForType: TypeDict[];
  tenderForType: TypeDict[];
  tenderExtensionType: TypeDict[];
  rfqStatus: TypeDict[];
  tenderDocumentTypes: TypeDict[];
  documentTypes: TypeDict[];
  purchaseClassifications: TypeDict[];
  purchaseStatuses: TypeDict[];
  tenderModeTypes: TypeDict[];
  currencies: Currency[];
}

const initialState: DictionaryState = {
  // mock requisition types dictionary
  requisitionRequestType: [
    {
      code: 'RequestType',
      codeNum: 2,
      codeDesc: 'Purchase Request',
      sortOrder: null,
      remark: null,
    },
    {
      code: 'RequestType',
      codeNum: 3,
      codeDesc: 'Tender Request',
      sortOrder: null,
      remark: null,
    },
  ],
  requestType: [],
  leaveType: [],
  leaveStatuses: [],
  itReqItems: [],
  simCapValue: [],
  itAccessories: [],
  passportType: [],
  passportStatuses: [],
  requisitionStatuses: [],
  salaryType: [],
  salaryStatuses: [],
  itReqStatuses: [],
  overseasStatuses: [],
  overseasSponsors: [],
  countries: [],
  companies: [],
  resumptionStatuses: [],
  resumptionWorkShift: [],
  attendanceReason: [],
  attendanceStatuses: [],
  attendanceType: [],
  workLocationType: [],
  projectNDepartments: [],
  departmentsProjects: [],
  deptProjList: [],
  resignationReasons: [],
  resignationStatuses: [],
  organizations: [],
  costCenterType: [],
  warrantyType: [],
  mrForType: [],
  tenderForType: [],
  tenderExtensionType: [],
  rfqStatus: [],
  tenderDocumentTypes: [],
  documentTypes: [],
  purchaseClassifications: [],
  purchaseStatuses: [],
  tenderModeTypes: [],
  currencies: [],
};

const dictionarySlice = createSlice({
  name: 'dictionary',
  initialState,
  reducers: {
    setRequestTypeDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.requestType = action.payload;
    },
    setLeaveTypeDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.leaveType = action.payload;
    },
    setLeaveStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.leaveStatuses = action.payload;
    },
    setITReqItemsDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.itReqItems = action.payload;
    },
    setSimCapValueDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.simCapValue = action.payload;
    },
    setITAccessories: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.itAccessories = action.payload;
    },
    setPassportTypeDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.passportType = action.payload;
    },
    setPassportStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.passportStatuses = action.payload;
    },
    setRequisitionStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.requisitionStatuses = action.payload;
    },
    setSalaryTypeDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.salaryType = action.payload;
    },
    setSalaryStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.salaryStatuses = action.payload;
    },
    setITReqStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.itReqStatuses = action.payload;
    },
    setOverseasStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.overseasStatuses = action.payload;
    },
    setOverseasSponsorsDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.overseasSponsors = action.payload;
    },
    setCompaniesDict: (
      state: DictionaryState,
      action: PayloadAction<CompanyDict[]>
    ) => {
      state.companies = action.payload;
    },
    setCountriesDict: (
      state: DictionaryState,
      action: PayloadAction<CountryDict[]>
    ) => {
      state.countries = action.payload;
    },
    setResumptionStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.resumptionStatuses = action.payload;
    },
    setResumptionWorkShiftDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.resumptionWorkShift = action.payload;
    },
    setAttendanceReasonDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.attendanceReason = action.payload;
    },
    setAttendanceStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.attendanceStatuses = action.payload;
    },
    setAttendanceTypeDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.attendanceType = action.payload;
    },
    setWorkLocationTypeDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.workLocationType = action.payload;
    },

    setProjectNDepartmentsDict: (
      state: DictionaryState,
      action: PayloadAction<ProjectNDepartments[]>
    ) => {
      state.projectNDepartments = action.payload;
    },
    setDepartmentsProjects: (
      state: DictionaryState,
      action: PayloadAction<ProjectNDepartments[]>
    ) => {
      state.departmentsProjects = action.payload;
    },

    setDeptProjList: (
      state: DictionaryState,
      action: PayloadAction<DeptProjList[]>
    ) => {
      state.deptProjList = action.payload;
    },
    setResignationStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.resignationStatuses = action.payload;
    },
    setOrganizationsDict: (
      state: DictionaryState,
      action: PayloadAction<Organization[]>
    ) => {
      state.organizations = action.payload;
    },
    setResignationReasonsDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.resignationReasons = action.payload;
    },
    setCostCenterType: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.costCenterType = action.payload;
    },
    setWarrantyType: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.warrantyType = action.payload;
    },
    setMrForType: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.mrForType = action.payload;
    },
    setTenderForType: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.tenderForType = action.payload;
    },
    setTenderExtensionType: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.tenderExtensionType = action.payload;
    },
    setRFQStatus: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.rfqStatus = action.payload;
    },
    setDocumentTypes: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.documentTypes = action.payload;
    },
    setTenderDocumentTypes: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.tenderDocumentTypes = action.payload;
    },
    setTenderModeTypes: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.tenderModeTypes = action.payload;
    },
    setPurchaseClassifications: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.purchaseClassifications = action.payload;
    },
    setPurchaseStatusesDict: (
      state: DictionaryState,
      action: PayloadAction<TypeDict[]>
    ) => {
      state.purchaseStatuses = action.payload;
    },
    setCurrencyDict: (
      state: DictionaryState,
      action: PayloadAction<Currency[]>
    ) => {
      state.currencies = action.payload;
    },
  },
});

const { actions } = dictionarySlice;

export const dictionaryActions = {
  ...actions,
};

export default dictionarySlice;
